// ── TrainingAdvancedTabs.tsx ──────────────────────────────────────────────────
// 4 advanced Training tabs — live data from NEXUS360_Production_Data Google Sheet
// Required tabs: training_parcours_agent | training_catalogue |
//                training_gaps_competences | training_plan_pluriannuel
// Sheet ID: 1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A (public Viewer)
//
// Usage in TrainingAnalyticsPage.tsx:
//   import { TrainingAdvancedTabs } from './TrainingAdvancedTabs';
//   <TrainingAdvancedTabs />   ← just before the Footer div
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BookOpen, Award, BarChart2, TrendingUp, Users, Shield,
  AlertTriangle, CheckCircle, Clock, DollarSign, Brain,
  Search, Filter, ChevronDown, ChevronUp, Star, Globe,
  Target, Calendar, Zap, AlertCircle, RefreshCw, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, ComposedChart, Line, ScatterChart, Scatter,
  ZAxis, ReferenceLine, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, PieChart, Pie
} from 'recharts';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Config ────────────────────────────────────────────────────────────────────
const MAIN_SHEET = '1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A';
const sheetUrl = (tab: string) =>
  `https://docs.google.com/spreadsheets/d/${MAIN_SHEET}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(tab)}`;
const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899','#84cc16','#f43f5e'];
const REG_PAGE_SIZE = 12;

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCsv(text: string): Record<string,string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g,'').trim());
  return lines.slice(1).map(line => {
    const vals: string[] = []; let cur = '', inQ = false;
    for (const c of line) {
      if (c==='"'){inQ=!inQ;continue;} if(c===','&&!inQ){vals.push(cur);cur='';continue;} cur+=c;
    }
    vals.push(cur);
    const row: Record<string,string> = {};
    headers.forEach((h,i) => { row[h]=(vals[i]||'').trim(); });
    return row;
  });
}

function useSheet<T>(tab: string, transform: (rows: Record<string,string>[]) => T[]) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetch_ = useCallback(async () => {
    setLoading(true); setError(false);
    try { const r = await fetch(sheetUrl(tab)); setData(transform(parseCsv(await r.text()))); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

const n = (v: any, fb=0) => { if(v===null||v===undefined||v==='') return fb; const x=Number(String(v).replace(',','.')); return isNaN(x)?fb:x; };
const fmtF = (v: number) => v>=1e9?(v/1e9).toFixed(1)+'B F':v>=1e6?(v/1e6).toFixed(1)+'M F':v>=1e3?(v/1e3).toFixed(0)+'K F':Math.round(v)+' F';
const pct = (a:number,b:number) => b===0?0:Math.round(a/b*1000)/10;

// ── Shared UI ─────────────────────────────────────────────────────────────────

// ── Wrapped X-axis tick (up to 3 lines, no rotation) ────────────────────────
const WrappedTick = ({x,y,payload}:any) => {
  const label = String(payload?.value||'');
  const words = label.split(' ');
  const lines: string[] = [];
  let cur = '';
  words.forEach((w:string) => {
    if((cur+' '+w).trim().length <= 12) { cur=(cur+' '+w).trim(); }
    else { if(cur) lines.push(cur); cur=w; }
  });
  if(cur) lines.push(cur);
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line:string, i:number) => (
        <text key={i} x={0} y={0} dy={10+i*11}
          textAnchor="middle" fill="#94a3b8" fontSize={8}>{line}</text>
      ))}
    </g>
  );
};

const NexTip = ({active,payload,label}:any) => {
  if(!active||!payload?.length) return null;
  return (
    <motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
      className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
      <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
      {payload.map((p:any,i:number) => (
        <div key={i} className="flex justify-between gap-3 py-0.5">
          <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div>
          <span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span>
        </div>
      ))}
    </motion.div>
  );
};

const Card = ({icon:Icon,title,children,accent='text-blue-400',className='',action}:any) => (
  <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.07)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white flex-1">{title}</span>{action}
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const KpiCard = ({icon:Icon,label,val,suffix='',dec=0,color,bg,border,alert=false}:any) => {
  const glow=color.includes('green')?'rgba(16,185,129,.2)':color.includes('red')?'rgba(239,68,68,.2)':color.includes('purple')?'rgba(168,85,247,.2)':color.includes('yellow')?'rgba(234,179,8,.2)':color.includes('cyan')?'rgba(6,182,212,.2)':'rgba(59,130,246,.2)';
  return (
    <motion.div whileHover={{scale:1.04,boxShadow:`0 0 16px ${glow}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-3 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,.85) 0%,rgba(30,41,59,.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-40"
        style={{background:`linear-gradient(90deg,transparent,${glow.replace('.2','.8').replace('rgba(','#').split(',')[0].replace('#','')},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 ${alert&&val>0?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-lg leading-none ${color}`}>
          <CountUp end={val} duration={1.5} decimals={dec} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&val>0&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

const PBar = ({value,color='bg-blue-500',delay=0}:{value:number;color?:string;delay?:number}) => (
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{width:0}} animate={{width:`${Math.min(100,value)}%`}}
      transition={{duration:1,ease:'easeOut',delay}}/>
  </div>
);

const LoadingBlock = () => (
  <div className="space-y-3 p-2">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={40} className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={20} count={5}/>
  </div>
);

const ErrorBlock = ({onRetry}:{onRetry:()=>void}) => (
  <div className="flex flex-col items-center gap-3 py-10 text-slate-400">
    <AlertCircle className="w-8 h-8 text-red-400"/>
    <p className="text-sm">Impossible de charger depuis Google Sheets.</p>
    <button onClick={onRetry} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs hover:bg-blue-500/30">
      <RefreshCw className="w-3.5 h-3.5"/>Réessayer
    </button>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — PARCOURS INDIVIDUEL AGENT
// ══════════════════════════════════════════════════════════════════════════════
interface ParcRow {
  parcours_id:string; employee_id:string; matricule:string; full_name:string;
  region:string; rank:string; formation_nom:string; type_formation:string;
  ecole:string; partenaire:string; date_debut:string; date_fin:string;
  promotion:string; score_examen_entree:number; score_examen_sortie:number;
  resultat:string; certif_obtenue:string; evaluation_a_chaud:number;
  evaluation_a_froid:number; score_perf_avant:number; score_perf_apres:number;
  uplift_perf_pct:number; cout_frais_fcfa:number; cout_indemnites_fcfa:number;
  cout_hebergement_fcfa:number; cout_transport_fcfa:number;
  cout_remplacement_fcfa:number; cout_total_fcfa:number;
}
const transformParc = (rows:Record<string,string>[]): ParcRow[] =>
  rows.map(r=>({...r,score_examen_entree:n(r.score_examen_entree),score_examen_sortie:n(r.score_examen_sortie),
    score_perf_avant:n(r.score_perf_avant),score_perf_apres:n(r.score_perf_apres),
    uplift_perf_pct:n(r.uplift_perf_pct),evaluation_a_chaud:n(r.evaluation_a_chaud),
    evaluation_a_froid:n(r.evaluation_a_froid),cout_frais_fcfa:n(r.cout_frais_fcfa),
    cout_indemnites_fcfa:n(r.cout_indemnites_fcfa),cout_hebergement_fcfa:n(r.cout_hebergement_fcfa),
    cout_transport_fcfa:n(r.cout_transport_fcfa),cout_remplacement_fcfa:n(r.cout_remplacement_fcfa),
    cout_total_fcfa:n(r.cout_total_fcfa)} as ParcRow));

const ParcoursAgent = () => {
  const {data,loading,error,refetch} = useSheet('training_parcours_agent',transformParc);
  const [search,setSearch] = useState('');
  const [typeF,setTypeF] = useState('');
  const [resultF,setResultF] = useState('');
  const [selAgent,setSelAgent] = useState<string|null>(null);
  const [regPage,setRegPage]=useState(0);

  const filtered = useMemo(()=>data.filter(r=>
    (!typeF||r.type_formation===typeF)&&(!resultF||r.resultat===resultF)&&
    (!search||[r.full_name,r.matricule,r.formation_nom].join(' ').toLowerCase().includes(search.toLowerCase()))
  ),[data,typeF,resultF,search]);

  const recus   = filtered.filter(r=>r.resultat==='Reçu').length;
  const recales = filtered.filter(r=>r.resultat==='Recalé').length;
  const totalCout = filtered.reduce((s,r)=>s+r.cout_total_fcfa,0);
  const avgUplift = filtered.length?Math.round(filtered.reduce((s,r)=>s+r.uplift_perf_pct,0)/filtered.length*10)/10:0;
  const certifs = filtered.filter(r=>r.certif_obtenue).length;

  // Agent detail
  const agentHistory = useMemo(()=>selAgent?data.filter(r=>r.employee_id===selAgent):[],[data,selAgent]);
  const agentName = agentHistory[0]?.full_name||'';

  // Promotion comparison
  const promoStats = useMemo(()=>{
    const m:Record<string,{scores:number[];uplifts:number[];count:number}> = {};
    filtered.forEach(r=>{if(!m[r.promotion])m[r.promotion]={scores:[],uplifts:[],count:0};
      m[r.promotion].scores.push(r.score_examen_sortie);m[r.promotion].uplifts.push(r.uplift_perf_pct);m[r.promotion].count++;});
    return Object.entries(m).map(([p,d])=>({promo:p.slice(0,18),score:Math.round(d.scores.reduce((s,v)=>s+v,0)/d.scores.length),uplift:Math.round(d.uplifts.reduce((s,v)=>s+v,0)/d.uplifts.length*10)/10,count:d.count})).sort((a,b)=>b.score-a.score).slice(0,8);
  },[data]);

  // Cost breakdown
  const coutMoyens = useMemo(()=>{
    if(!filtered.length) return [];
    const avg=(key:keyof ParcRow)=>Math.round(filtered.reduce((s,r)=>s+n(r[key]),0)/filtered.length/1000);
    return [
      {name:'Frais pédagogiques',val:avg('cout_frais_fcfa'),fill:'#3b82f6'},
      {name:'Indemnités',val:avg('cout_indemnites_fcfa'),fill:'#10b981'},
      {name:'Hébergement',val:avg('cout_hebergement_fcfa'),fill:'#f97316'},
      {name:'Transport',val:avg('cout_transport_fcfa'),fill:'#a855f7'},
      {name:'Remplacement',val:avg('cout_remplacement_fcfa'),fill:'#eab308'},
    ];
  },[data]);

  const ecoles = [...new Set(data.map(r=>r.ecole))].sort();
  const types  = [...new Set(data.map(r=>r.type_formation))].sort();
  const agents = [...new Set(data.map(r=>({id:r.employee_id,name:r.full_name})).map(a=>JSON.stringify(a)))].map(s=>JSON.parse(s));

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Users}      label="Parcours total"       val={filtered.length} color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KpiCard icon={CheckCircle} label="Reçus / certifiés"   val={recus}       color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KpiCard icon={Award}      label="Certifications"       val={certifs}     color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
        <KpiCard icon={TrendingUp} label="Uplift perf. moy."    val={avgUplift}   suffix="%" dec={1} color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
        <KpiCard icon={DollarSign} label="Coût total (M F)"     val={Math.round(totalCout/1e6)} color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30"/>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setRegPage(0);}} placeholder="Nom / formation…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:typeF,s:setTypeF,o:types,p:'Type'},{v:resultF,s:setResultF,o:['Reçu','Recalé','Abandon'],p:'Résultat'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        <select value={selAgent||''} onChange={e=>setSelAgent(e.target.value||null)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
          <option value="">Voir parcours agent…</option>{agents.map((a:any)=><option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {(search||typeF||resultF||selAgent)&&<button onClick={()=>{setSearch('');setTypeF('');setResultF('');setSelAgent(null);setRegPage(0);}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} parcours</span>
      </div>

      {/* Agent detail panel */}
      <AnimatePresence>
        {selAgent&&agentHistory.length>0&&(
          <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}}
            className="rounded-xl border border-blue-500/40 overflow-hidden"
            style={{background:'linear-gradient(135deg,rgba(30,58,138,.2) 0%,rgba(15,23,42,.9) 100%)',backdropFilter:'blur(8px)'}}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-blue-500/20">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                {agentName.split(' ').map((w:string)=>w[0]).join('').slice(0,2)}
              </div>
              <div><p className="text-white font-semibold">{agentName}</p><p className="text-slate-400 text-xs">{agentHistory[0]?.rank} · {agentHistory[0]?.region} · {agentHistory.length} formation(s)</p></div>
              <button onClick={()=>setSelAgent(null)} className="ml-auto text-slate-400 hover:text-white">✕</button>
            </div>
            <div className="p-4 overflow-x-auto">
              <div className="flex gap-3 min-w-max">
                {agentHistory.map((r,i)=>(
                  <motion.div key={r.parcours_id} initial={{opacity:0,x:20}} animate={{opacity:1,x:0}} transition={{delay:i*.08}}
                    className={`rounded-lg border p-3 min-w-52 ${r.resultat==='Reçu'?'border-green-500/30 bg-green-500/10':r.resultat==='Recalé'?'border-red-500/30 bg-red-500/10':'border-slate-600/40 bg-slate-700/40'}`}>
                    <p className="text-white text-xs font-bold mb-1 truncate">{r.formation_nom}</p>
                    <p className="text-slate-400 text-xs mb-2">{r.date_debut?.slice(0,7)} · {r.ecole}</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {[['Entrée',r.score_examen_entree+'/100','text-slate-300'],['Sortie',r.score_examen_sortie+'/100',r.score_examen_sortie>=50?'text-green-400':'text-red-400'],['Éval. chaud',r.evaluation_a_chaud+'/100','text-blue-300'],['Uplift perf.','+'+r.uplift_perf_pct+'%','text-purple-400']].map(([k,v,c])=>(
                        <div key={k}><p className="text-slate-500" style={{fontSize:'9px'}}>{k}</p><p className={`font-bold text-xs ${c}`}>{v}</p></div>
                      ))}
                    </div>
                    {r.certif_obtenue&&<p className="text-yellow-400 text-xs mt-2 truncate">🏅 {r.certif_obtenue}</p>}
                    <p className="text-slate-500 text-xs mt-1">{fmtF(r.cout_total_fcfa)}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Promotion comparison */}
        <div className="lg:col-span-2">
        <Card icon={BarChart2} title="Comparaison inter-promotions — Score moyen + Uplift perf." accent="text-blue-400">
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={promoStats} margin={{top:8,right:0,left:0,bottom:4}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="promo" tick={<WrappedTick/>} interval={0} tickLine={false} height={44} padding={{left:16,right:16}}/>
              <YAxis yAxisId="left" tick={{fill:'#94a3b8',fontSize:9}} domain={[0,100]}/>
              <YAxis yAxisId="right" orientation="right" tick={{fill:'#a855f7',fontSize:9}} tickFormatter={v=>`${v}%`}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'9px'}} verticalAlign="top"/>
              <Bar yAxisId="left" dataKey="score" name="Score sortie moyen" fill="#3b82f6" radius={[3,3,0,0]} isAnimationActive animationDuration={900}/>
              <Line yAxisId="right" type="monotone" dataKey="uplift" name="Uplift perf. (%)" stroke="#a855f7" strokeWidth={2.5} dot={{r:4,fill:'#a855f7'}} isAnimationActive animationDuration={1400}/>
              <ReferenceLine yAxisId="left" y={50} stroke="#eab308" strokeDasharray="4 2" label={{value:'Seuil réussite',fill:'#eab308',fontSize:8,position:'insideTopRight'}}/>
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
        </div>

        {/* Coût moyen par poste */}
        <Card icon={DollarSign} title="Coût moyen par agent — Décomposition 5 postes (K FCFA)" accent="text-orange-400">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={coutMoyens} margin={{top:8,right:8,left:0,bottom:8}} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="name" tick={{fill:'#94a3b8',fontSize:8}} angle={-15} textAnchor="end" height={40} interval={0}/>
              <YAxis tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}K`}/>
              <Tooltip content={<NexTip/>} formatter={(v:any)=>`${v}K F`}/>
              <Bar dataKey="val" name="Coût moyen (K F)" radius={[4,4,0,0]} isAnimationActive animationDuration={1000}>
                {coutMoyens.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Réussite par école */}
      <Card icon={BookOpen} title="Taux de réussite par école — Comparaison inter-établissements" accent="text-yellow-400">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {ecoles.map((ecole,i) => {
              const sub = filtered.filter(r=>r.ecole===ecole);
              const tauxReussite = sub.length?Math.round(sub.filter(r=>r.resultat==='Reçu').length/sub.length*100):0;
              const avgScore = sub.length?Math.round(sub.reduce((s,r)=>s+r.score_examen_sortie,0)/sub.length):0;
              const avgUpliftE = sub.length?Math.round(sub.reduce((s,r)=>s+r.uplift_perf_pct,0)/sub.length*10)/10:0;
              return (
                <motion.div key={ecole} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.06}}
                  whileHover={{scale:1.02,boxShadow:`0 0 12px ${PAL[i%PAL.length]}33`}}
                  className="rounded-xl border border-blue-900/40 p-4"
                  style={{background:'linear-gradient(135deg,rgba(15,23,42,.9) 0%,rgba(30,41,59,.7) 100%)'}}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{background:`${PAL[i%PAL.length]}22`}}>
                    <BookOpen className="w-4 h-4" style={{color:PAL[i%PAL.length]}}/>
                  </div>
                  <p className="text-white text-xs font-semibold mb-3 leading-tight">{ecole}</p>
                  {[['Agents formés',sub.length,'text-slate-300'],['Taux réussite',tauxReussite+'%',tauxReussite>=80?'text-green-400':tauxReussite>=60?'text-yellow-400':'text-red-400'],['Score moy.',avgScore+'/100','text-blue-300'],['Uplift moy.','+'+avgUpliftE+'%','text-purple-400']].map(([k,v,c])=>(
                    <div key={k} className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">{k}</span><span className={`font-bold ${c}`}>{v}</span>
                    </div>
                  ))}
                  <div className="mt-2">
                    <PBar value={tauxReussite} color={tauxReussite>=80?'bg-green-500':tauxReussite>=60?'bg-yellow-500':'bg-red-500'} delay={i*.06}/>
                  </div>
                </motion.div>
              );
            })}
        </div>
      </Card>

      {/* Full parcours table */}
      <Card icon={Eye} title="Registre complet parcours agents" accent="text-blue-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight:'420px'}}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['Matricule','Nom','Grade','Région','Formation','Type','École','Partenaire','Promotion','Entrée','Sortie','Résultat','Certif.','Éval.chaud','Uplift perf.','Coût total'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(regPage*REG_PAGE_SIZE,(regPage+1)*REG_PAGE_SIZE).map((r,i)=>(
                  <motion.tr key={r.parcours_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                    whileHover={{backgroundColor:'rgba(59,130,246,.05)'}}
                    onClick={()=>setSelAgent(r.employee_id===selAgent?null:r.employee_id)}
                    className={`border-b border-blue-900/30 transition-colors cursor-pointer ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.matricule}</td>
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{r.full_name}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-white max-w-44 truncate" title={r.formation_nom}>{r.formation_nom}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap max-w-36 truncate">{r.type_formation}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap max-w-36 truncate">{r.ecole}</td>
                    <td className="px-3 py-2">
                      {r.partenaire!=='National'?<span className="text-cyan-400 font-medium text-xs flex items-center gap-1"><Globe className="w-3 h-3"/>{r.partenaire}</span>:<span className="text-slate-500">{r.partenaire}</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.promotion}</td>
                    <td className="px-3 py-2 text-center text-slate-400">{r.score_examen_entree}</td>
                    <td className="px-3 py-2 text-center"><span className={r.score_examen_sortie>=50?'text-green-400 font-bold':r.score_examen_sortie>=40?'text-yellow-400':'text-red-400'}>{r.score_examen_sortie}</span></td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${r.resultat==='Reçu'?'bg-green-500/10 border-green-500/30 text-green-400':r.resultat==='Recalé'?'bg-red-500/10 border-red-500/30 text-red-400':'bg-slate-500/10 border-slate-500/30 text-slate-400'}`}
                        animate={r.resultat==='Recalé'?{opacity:[1,.7,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                        {r.resultat}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 text-yellow-400 text-xs max-w-32 truncate">{r.certif_obtenue||'—'}</td>
                    <td className="px-3 py-2 text-center text-blue-300">{r.evaluation_a_chaud||'—'}</td>
                    <td className="px-3 py-2 text-center"><span className={r.uplift_perf_pct>0?'text-green-400 font-bold':r.uplift_perf_pct<0?'text-red-400':'text-slate-400'}>{r.uplift_perf_pct>0?'+':''}{r.uplift_perf_pct}%</span></td>
                    <td className="px-3 py-2 text-right text-orange-400 whitespace-nowrap font-medium">{fmtF(r.cout_total_fcfa)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Pagination footer */}
        <div className="px-4 py-2.5 border-t border-blue-900/40 flex items-center justify-between"
          style={{background:'rgba(8,12,24,.95)'}}>
          <span className="text-xs text-slate-500">
            {filtered.length} parcours · page{' '}
            <span className="text-blue-400 font-semibold">{regPage+1}</span>
            /{Math.max(1,Math.ceil(filtered.length/REG_PAGE_SIZE))}
          </span>
          <div className="flex items-center gap-1.5">
            <button onClick={()=>setRegPage(p=>Math.max(0,p-1))} disabled={regPage===0}
              className="px-2 py-1 rounded-lg border border-slate-700/40 text-slate-400 hover:bg-slate-700 disabled:opacity-25 text-xs">‹</button>
            {Array.from({length:Math.min(Math.ceil(filtered.length/REG_PAGE_SIZE),7)},(_,i)=>{
              const total=Math.ceil(filtered.length/REG_PAGE_SIZE);
              let pg=i;
              if(total>7){
                if(i===0) pg=0;
                else if(i===6) pg=total-1;
                else pg=Math.max(1,Math.min(regPage-1,total-5))+i-1;
              }
              return pg>=0&&pg<total?(
                <button key={pg} onClick={()=>setRegPage(pg)}
                  className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${pg===regPage?'bg-blue-600 text-white shadow-md':'text-slate-500 hover:bg-slate-700 border border-slate-700/40'}`}>
                  {pg+1}
                </button>
              ):null;
            })}
            <button onClick={()=>setRegPage(p=>Math.min(Math.ceil(filtered.length/REG_PAGE_SIZE)-1,p+1))}
              disabled={regPage>=Math.ceil(filtered.length/REG_PAGE_SIZE)-1}
              className="px-2 py-1 rounded-lg border border-slate-700/40 text-slate-400 hover:bg-slate-700 disabled:opacity-25 text-xs">›</button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — CATALOGUE CENTRALISÉ
// ══════════════════════════════════════════════════════════════════════════════
interface CatRow {
  formation_id:string; formation_nom:string; type_formation:string; categorie:string;
  ecole:string; partenaire_international:string; niveau_requis:string;
  duree_jours:number; capacite_max:number; promotions_dispensees:number;
  cout_total_unitaire_fcfa:number; taux_reussite_historique_pct:number;
  roi_moyen_pct:number; certif_delivree:string; valide_interpol:string;
  valide_ue:string; valide_onudc:string; prerequis:string; statut:string;
}
const transformCat = (rows:Record<string,string>[]): CatRow[] =>
  rows.map(r=>({...r,duree_jours:n(r.duree_jours),capacite_max:n(r.capacite_max),
    promotions_dispensees:n(r.promotions_dispensees),cout_total_unitaire_fcfa:n(r.cout_total_unitaire_fcfa),
    taux_reussite_historique_pct:n(r.taux_reussite_historique_pct),roi_moyen_pct:n(r.roi_moyen_pct)} as CatRow));

const CataloguePlusFormations = () => {
  const {data,loading,error,refetch} = useSheet('training_catalogue',transformCat);
  const [search,setSearch] = useState('');
  const [partF,setPartF] = useState('');
  const [catF,setCatF] = useState('');
  const [sortKey,setSortKey] = useState<'roi'|'taux'|'cout'>('roi');
  const [sortAsc,setSortAsc] = useState(false);

  const filtered = useMemo(()=>[...data].filter(r=>
    (!partF||r.partenaire_international===partF)&&(!catF||r.categorie===catF)&&
    (!search||[r.formation_nom,r.ecole,r.partenaire_international].join(' ').toLowerCase().includes(search.toLowerCase()))
  ).sort((a,b)=>{
    const va=sortKey==='roi'?a.roi_moyen_pct:sortKey==='taux'?a.taux_reussite_historique_pct:a.cout_total_unitaire_fcfa;
    const vb=sortKey==='roi'?b.roi_moyen_pct:sortKey==='taux'?b.taux_reussite_historique_pct:b.cout_total_unitaire_fcfa;
    return sortAsc?va-vb:vb-va;
  }),[data,partF,catF,search,sortKey,sortAsc]);

  const interpol = filtered.filter(r=>r.valide_interpol==='OUI').length;
  const ue       = filtered.filter(r=>r.valide_ue==='OUI').length;
  const onudc    = filtered.filter(r=>r.valide_onudc==='OUI').length;
  const avgROI   = filtered.length?Math.round(filtered.reduce((s,r)=>s+r.roi_moyen_pct,0)/filtered.length):0;

  const partenaires = [...new Set(data.map(r=>r.partenaire_international))].sort();
  const cats = [...new Set(data.map(r=>r.categorie))].sort();

  // ROI by partenaire
  const roiByPart = useMemo(()=>partenaires.map((p,i)=>{
    const sub=filtered.filter(r=>r.partenaire_international===p);
    return{partenaire:p.replace('Académie','Acad.').slice(0,20),roi:sub.length?Math.round(sub.reduce((s,r)=>s+r.roi_moyen_pct,0)/sub.length):0,count:sub.length,fill:PAL[i%PAL.length]};
  }).sort((a,b)=>b.roi-a.roi),[data,partenaires]);

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={BookOpen}   label="Formations catalogue"   val={filtered.length}  color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KpiCard icon={TrendingUp} label="ROI moyen (%)"          val={avgROI}        color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KpiCard icon={Globe}      label="Validées Interpol"       val={interpol}      color="text-cyan-400"   bg="bg-cyan-500/10"   border="border-cyan-500/30"/>
        <KpiCard icon={Shield}     label="Validées UE/ONUDC"      val={ue+onudc}      color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
      </div>

      {/* International badges */}
      <div className="grid grid-cols-3 gap-3">
        {[
          {label:'Interpol Lyon',n:interpol,color:'#3b82f6',icon:'🔵'},
          {label:'Union Européenne',n:ue,color:'#eab308',icon:'🇪🇺'},
          {label:'ONUDC Vienne',n:onudc,color:'#10b981',icon:'🌐'},
        ].map((p,i)=>(
          <motion.div key={p.label} whileHover={{scale:1.03}} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.06}}
            className="rounded-xl border p-3 flex items-center gap-3" style={{borderColor:`${p.color}44`,background:`linear-gradient(135deg,${p.color}11 0%,rgba(15,23,42,.8) 100%)`}}>
            <span className="text-2xl">{p.icon}</span>
            <div>
              <p className="font-black text-2xl" style={{color:p.color}}><CountUp end={p.n} duration={1.5} preserveValue/></p>
              <p className="text-slate-400 text-xs">{p.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ROI by partenaire */}
      <Card icon={BarChart2} title="ROI moyen par partenaire — Meilleur investissement formation" accent="text-green-400">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={roiByPart} margin={{top:4,right:0,left:0,bottom:0}} barCategoryGap="25%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
            <XAxis dataKey="partenaire" tick={<WrappedTick/>} interval={0} tickLine={false} height={44} padding={{left:16,right:16}}/>
            <YAxis tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}%`}/>
            <Tooltip content={<NexTip/>} formatter={(v:any)=>`${v}%`}/>
            <Bar dataKey="roi" name="ROI moyen (%)" radius={[4,4,0,0]} isAnimationActive animationDuration={1000} legendType="none">
              {roiByPart.map((d,i)=><Cell key={i} fill={d.fill}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setRegPage(0);}} placeholder="Formation / école…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:partF,s:setPartF,o:partenaires,p:'Partenaire'},{v:catF,s:setCatF,o:cats,p:'Catégorie'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <span className="text-slate-400 text-xs">Trier:</span>
          {[{k:'roi',l:'ROI'},{k:'taux',l:'Réussite'},{k:'cout',l:'Coût'}].map(s=>(
            <button key={s.k} onClick={()=>{if(sortKey===s.k)setSortAsc(a=>!a);else{setSortKey(s.k as any);setSortAsc(false);}}}
              className={`px-2 py-0.5 rounded text-xs border transition-all flex items-center gap-0.5 ${sortKey===s.k?'bg-blue-500/20 text-blue-300 border-blue-500/40':'border-slate-600/40 text-slate-400 hover:bg-slate-700'}`}>
              {s.l}{sortKey===s.k?(sortAsc?<ChevronUp className="w-3 h-3"/>:<ChevronDown className="w-3 h-3"/>):null}
            </button>
          ))}
        </div>
        {(search||partF||catF)&&<button onClick={()=>{setSearch('');setPartF('');setCatF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} formations</span>
      </div>

      {/* Catalogue grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.slice(0,12).map((r,i)=>(
          <motion.div key={r.formation_id} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.04}}
            whileHover={{scale:1.02,boxShadow:'0 0 14px rgba(59,130,246,.1)'}}
            className="rounded-xl border border-blue-900/40 p-3"
            style={{background:'linear-gradient(135deg,rgba(15,23,42,.9) 0%,rgba(30,41,59,.65) 100%)'}}>
            <div className="flex items-start justify-between mb-2">
              <p className="text-white text-xs font-semibold leading-tight max-w-44">{r.formation_nom}</p>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ml-1 ${r.statut==='Actif'?'text-green-400 bg-green-500/10':r.statut==='Planifié'?'text-blue-400 bg-blue-500/10':'text-slate-400 bg-slate-500/10'}`}>{r.statut}</span>
            </div>
            <p className="text-slate-400 text-xs mb-2">{r.ecole} · {r.duree_jours}j · {r.niveau_requis}</p>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {r.partenaire_international!=='National'&&<span className="text-xs bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 px-1.5 py-0.5 rounded-full flex items-center gap-1"><Globe className="w-2.5 h-2.5"/>{r.partenaire_international}</span>}
              {r.valide_interpol==='OUI'&&<span className="text-xs bg-blue-500/10 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full">Interpol</span>}
              {r.valide_ue==='OUI'&&<span className="text-xs bg-yellow-500/10 text-yellow-300 border border-yellow-500/20 px-1.5 py-0.5 rounded-full">UE</span>}
            </div>
            <div className="grid grid-cols-3 gap-1 text-xs">
              <div><p className="text-slate-500" style={{fontSize:'9px'}}>Réussite</p><p className={`font-bold ${r.taux_reussite_historique_pct>=80?'text-green-400':r.taux_reussite_historique_pct>=60?'text-yellow-400':'text-red-400'}`}>{r.taux_reussite_historique_pct}%</p></div>
              <div><p className="text-slate-500" style={{fontSize:'9px'}}>ROI moy.</p><p className="text-green-400 font-bold">{r.roi_moyen_pct}%</p></div>
              <div><p className="text-slate-500" style={{fontSize:'9px'}}>Coût/agent</p><p className="text-orange-400 font-bold">{Math.round(r.cout_total_unitaire_fcfa/1000)}K F</p></div>
            </div>
            <PBar value={r.taux_reussite_historique_pct} color={r.taux_reussite_historique_pct>=80?'bg-green-500':r.taux_reussite_historique_pct>=60?'bg-yellow-500':'bg-red-500'} delay={i*.04}/>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — GAPS DE COMPÉTENCES
// ══════════════════════════════════════════════════════════════════════════════
interface GapRow {
  gap_id:string; employee_id:string; matricule:string; full_name:string;
  region:string; rank:string; poste_actuel:string; competence_requise:string;
  niveau_requis_poste:string; niveau_certifie_agent:string; gap_score:number;
  statut_gap:string; formation_recommandee:string; priorite:string;
  delai_recommande_mois:number; cout_combler_gap_fcfa:number;
  certifs_detenues:string; certifs_manquantes:string;
}
const transformGap = (rows:Record<string,string>[]): GapRow[] =>
  rows.map(r=>({...r,gap_score:n(r.gap_score),delai_recommande_mois:n(r.delai_recommande_mois),cout_combler_gap_fcfa:n(r.cout_combler_gap_fcfa)} as GapRow));

const GapsCompetences = () => {
  const [gapsPage,setGapsPage]=useState(0);
  const {data,loading,error,refetch} = useSheet('training_gaps_competences',transformGap);
  const [search,setSearch] = useState('');
  const [prioF,setPrioF] = useState('');
  const [compF,setCompF] = useState('');

  const filtered = useMemo(()=>data.filter(r=>
    (!prioF||r.priorite===prioF)&&(!compF||r.competence_requise===compF)&&
    (!search||[r.full_name,r.matricule,r.poste_actuel].join(' ').toLowerCase().includes(search.toLowerCase()))
  ).sort((a,b)=>b.gap_score-a.gap_score),[data,prioF,compF,search]);

  const critiques = filtered.filter(r=>r.statut_gap==='CRITIQUE').length;
  const moderes   = filtered.filter(r=>r.statut_gap==='MODÉRÉ').length;
  const totalCout = filtered.reduce((s,r)=>s+r.cout_combler_gap_fcfa,0);
  const sansgap   = filtered.filter(r=>r.gap_score===0).length;

  const compStats = useMemo(()=>{
    const m:Record<string,{critique:number;modere:number;ok:number}> = {};
    filtered.forEach(r=>{if(!m[r.competence_requise])m[r.competence_requise]={critique:0,modere:0,ok:0};
      if(r.statut_gap==='CRITIQUE')m[r.competence_requise].critique++;
      else if(r.statut_gap==='MODÉRÉ')m[r.competence_requise].modere++;
      else m[r.competence_requise].ok++;});
    return Object.entries(m).map(([name,d])=>({name:name.slice(0,22),...d})).sort((a,b)=>b.critique-a.critique).slice(0,8);
  },[data]);

  const competences = [...new Set(data.map(r=>r.competence_requise))].sort();

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* Alert banner */}
      {critiques>0&&(
        <motion.div className="rounded-xl border border-red-500/40 p-3 flex items-start gap-3 relative overflow-hidden"
          style={{background:'linear-gradient(135deg,rgba(127,29,29,.2) 0%,rgba(15,23,42,.8) 100%)'}}>
          <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
            animate={{boxShadow:['0 0 0px rgba(239,68,68,0)','0 0 20px rgba(239,68,68,.15)','0 0 0px rgba(239,68,68,0)']}}
            transition={{duration:2,repeat:Infinity}}/>
          <motion.div animate={{rotate:[0,-8,8,-8,0]}} transition={{duration:.5,repeat:Infinity,repeatDelay:3}}>
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0"/>
          </motion.div>
          <div className="flex-1 relative z-10">
            <p className="text-red-300 font-bold text-sm mb-1">⚠ {critiques} gaps CRITIQUES — Plan de formation prioritaire requis</p>
            <div className="flex flex-wrap gap-2">
              {filtered.filter(r=>r.statut_gap==='CRITIQUE').map((r,i)=>(
                <motion.span key={i} className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30"
                  animate={{opacity:[1,.6,1]}} transition={{duration:1.5,repeat:Infinity,delay:i*.2}}>
                  {r.full_name} · {r.competence_requise} · {r.formation_recommandee?.slice(0,25)}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={AlertTriangle} label="Gaps critiques"       val={critiques} color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
        <KpiCard icon={AlertCircle}   label="Gaps modérés"         val={moderes}   color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30"/>
        <KpiCard icon={CheckCircle}   label="Sans gap (OK)"        val={sansgap}   color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KpiCard icon={DollarSign}    label="Budget combler gaps"  val={Math.round(totalCout/1e6)} suffix="M F" color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Gaps par compétence */}
        <Card icon={BarChart2} title="Gaps par compétence — Priorité d'action" accent="text-red-400">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={compStats} layout="vertical" margin={{top:4,right:8,left:8,bottom:4}} barCategoryGap="18%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false}/>
              <YAxis type="category" dataKey="name" tick={{fill:'#cbd5e1',fontSize:8}} width={130}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{fontSize:'9px'}}/>
              <Bar dataKey="critique" name="Critique" stackId="a" fill="#ef4444" isAnimationActive animationDuration={900}/>
              <Bar dataKey="modere"   name="Modéré"   stackId="a" fill="#f97316" isAnimationActive animationDuration={1100}/>
              <Bar dataKey="ok"       name="OK"        stackId="a" fill="#10b981" radius={[0,3,3,0]} isAnimationActive animationDuration={1300}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top agents prioritaires */}
        <Card icon={Target} title="Top 10 agents — Plan de formation prioritaire" accent="text-orange-400">
          <div className="space-y-2 overflow-y-auto" style={{maxHeight:'280px'}}>
            {filtered.filter(r=>r.gap_score>0).slice(0,10).map((r,i)=>(
              <motion.div key={r.gap_id} initial={{opacity:0,x:8}} animate={{opacity:1,x:0}} transition={{delay:i*.05}}
                whileHover={{scale:1.01,boxShadow:r.statut_gap==='CRITIQUE'?'0 0 10px rgba(239,68,68,.15)':undefined}}
                className={`p-2.5 rounded-lg border ${r.statut_gap==='CRITIQUE'?'bg-red-500/10 border-red-500/30':r.statut_gap==='MODÉRÉ'?'bg-orange-500/10 border-orange-500/30':'bg-slate-700/40 border-slate-600/40'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-xs font-medium">{r.full_name}</span>
                  <motion.span className={`text-xs font-bold ${r.statut_gap==='CRITIQUE'?'text-red-400':r.statut_gap==='MODÉRÉ'?'text-orange-400':'text-green-400'}`}
                    animate={r.statut_gap==='CRITIQUE'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                    GAP {r.gap_score}
                  </motion.span>
                </div>
                <p className="text-slate-400 text-xs mb-1">{r.competence_requise} · Requis: <span className="text-blue-300">{r.niveau_requis_poste}</span> · Certifié: <span className="text-purple-300">{r.niveau_certifie_agent}</span></p>
                <p className="text-cyan-400 text-xs truncate">→ {r.formation_recommandee}</p>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-slate-500">Délai: {r.delai_recommande_mois} mois</span>
                  <span className="text-orange-400">{fmtF(r.cout_combler_gap_fcfa)}</span>
                </div>
                <PBar value={r.gap_score} color={r.gap_score>=50?'bg-red-500':r.gap_score>=25?'bg-orange-500':'bg-green-500'} delay={i*.05}/>
              </motion.div>
            ))}
          </div>
        </Card>
      </div>

      {/* Filters + table */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setRegPage(0);}} placeholder="Nom / poste…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:prioF,s:setPrioF,o:['HAUTE','MOYENNE','BASSE'],p:'Priorité'},{v:compF,s:setCompF,o:competences,p:'Compétence'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        {(search||prioF||compF)&&<button onClick={()=>{setSearch('');setPrioF('');setCompF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} gaps</span>
      </div>

      <Card icon={Brain} title="Registre gaps — Plan de formation auto-généré · Google Sheets live" accent="text-red-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight:'380px'}}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['Matricule','Nom','Grade','Région','Poste','Compétence','Requis','Certifié','Gap','Statut','Formation recommandée','Priorité','Délai','Coût','Certifs manquantes'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(gapsPage*REG_PAGE_SIZE,(gapsPage+1)*REG_PAGE_SIZE).map((r,i)=>(
                  <motion.tr key={r.gap_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                    whileHover={{backgroundColor:'rgba(59,130,246,.05)'}}
                    className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.matricule}</td>
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{r.full_name}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-36 truncate">{r.poste_actuel}</td>
                    <td className="px-3 py-2 text-blue-300 whitespace-nowrap">{r.competence_requise}</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.niveau_requis_poste}</td>
                    <td className="px-3 py-2 text-center text-purple-300">{r.niveau_certifie_agent}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="relative w-9 h-9 mx-auto">
                        <svg viewBox="0 0 36 36" className="-rotate-90 w-9 h-9">
                          <circle cx="18" cy="18" r="14" fill="none" stroke="#1e293b" strokeWidth="3"/>
                          <motion.circle cx="18" cy="18" r="14" fill="none"
                            stroke={r.gap_score>=50?'#ef4444':r.gap_score>=25?'#f97316':'#10b981'}
                            strokeWidth="3" initial={{strokeDasharray:'0 88'}}
                            animate={{strokeDasharray:`${r.gap_score*.88} 88`}}
                            transition={{duration:1.2,ease:'easeOut',delay:i*.02}} strokeLinecap="round"/>
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">{r.gap_score}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${r.statut_gap==='CRITIQUE'?'bg-red-500/10 border-red-500/30 text-red-400':r.statut_gap==='MODÉRÉ'?'bg-orange-500/10 border-orange-500/30 text-orange-400':'bg-green-500/10 border-green-500/30 text-green-400'}`}
                        animate={r.statut_gap==='CRITIQUE'?{opacity:[1,.7,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                        {r.statut_gap}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 text-cyan-400 max-w-44 truncate">{r.formation_recommandee}</td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs font-bold ${r.priorite==='HAUTE'?'text-red-400':r.priorite==='MOYENNE'?'text-orange-400':'text-slate-400'}`}>{r.priorite}</span></td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.delai_recommande_mois} mois</td>
                    <td className="px-3 py-2 text-right text-orange-400 whitespace-nowrap">{r.cout_combler_gap_fcfa>0?fmtF(r.cout_combler_gap_fcfa):'—'}</td>
                    <td className="px-3 py-2 text-red-300 max-w-48 truncate text-xs">{r.certifs_manquantes||'—'}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 4 — PLAN PLURIANNUEL 3 ANS
// ══════════════════════════════════════════════════════════════════════════════
interface PlanRow {
  plan_id:string; region:string; corps:string; rank:string;
  annee:number; type_formation:string; ecole:string; partenaire:string;
  nb_agents_a_former:number; nb_sessions_prevues:number; duree_session_jours:number;
  cout_unitaire_fcfa:number; budget_total_fcfa:number; budget_alloue_fcfa:number;
  ecart_budget_fcfa:number; priorite_dg:string; objectif_certif:string;
  taux_cible_pct:number; statut_plan:string;
}
const transformPlan = (rows:Record<string,string>[]): PlanRow[] =>
  rows.map(r=>({...r,annee:n(r.annee),nb_agents_a_former:n(r.nb_agents_a_former),
    nb_sessions_prevues:n(r.nb_sessions_prevues),duree_session_jours:n(r.duree_session_jours),
    cout_unitaire_fcfa:n(r.cout_unitaire_fcfa),budget_total_fcfa:n(r.budget_total_fcfa),
    budget_alloue_fcfa:n(r.budget_alloue_fcfa),ecart_budget_fcfa:n(r.ecart_budget_fcfa),
    taux_cible_pct:n(r.taux_cible_pct)} as PlanRow));

const PlanPluriannel = () => {
  const [planPage,setPlanPage]=useState(0);
  const {data,loading,error,refetch} = useSheet('training_plan_pluriannuel',transformPlan);
  const [anneeF,setAnneeF] = useState('');
  const [regionF,setRegionF] = useState('');
  const [prioF,setPrioF] = useState('');

  const filtered = useMemo(()=>data.filter(r=>
    (!anneeF||String(r.annee)===anneeF)&&(!regionF||r.region===regionF)&&(!prioF||r.priorite_dg===prioF)
  ),[data,anneeF,regionF,prioF]);

  const total2026 = filtered.filter(r=>r.annee===2026).reduce((s,r)=>s+r.budget_total_fcfa,0);
  const total2027 = filtered.filter(r=>r.annee===2027).reduce((s,r)=>s+r.budget_total_fcfa,0);
  const total2028 = filtered.filter(r=>r.annee===2028).reduce((s,r)=>s+r.budget_total_fcfa,0);
  const totalAlloue = filtered.reduce((s,r)=>s+r.budget_alloue_fcfa,0);
  const totalBesoin = filtered.reduce((s,r)=>s+r.budget_total_fcfa,0);
  const deficit = totalBesoin-totalAlloue;
  const totalAgents = filtered.reduce((s,r)=>s+r.nb_agents_a_former,0);

  // Budget by year
  const budgetAnnuel = [
    {annee:'2026',besoin:Math.round(total2026/1e6),alloue:Math.round(filtered.filter(r=>r.annee===2026).reduce((s,r)=>s+r.budget_alloue_fcfa,0)/1e6)},
    {annee:'2027',besoin:Math.round(total2027/1e6),alloue:Math.round(filtered.filter(r=>r.annee===2027).reduce((s,r)=>s+r.budget_alloue_fcfa,0)/1e6)},
    {annee:'2028',besoin:Math.round(total2028/1e6),alloue:Math.round(filtered.filter(r=>r.annee===2028).reduce((s,r)=>s+r.budget_alloue_fcfa,0)/1e6)},
  ];

  // Priorités DG
  const prioDG = useMemo(()=>{
    const m:Record<string,{agents:number;budget:number}> = {};
    filtered.forEach(r=>{if(!m[r.priorite_dg])m[r.priorite_dg]={agents:0,budget:0};
      m[r.priorite_dg].agents+=r.nb_agents_a_former;m[r.priorite_dg].budget+=r.budget_total_fcfa;});
    return Object.entries(m).map(([p,d],i)=>({prio:p,agents:d.agents,budget:Math.round(d.budget/1e6),fill:PAL[i%PAL.length]})).sort((a,b)=>b.agents-a.agents);
  },[data]);

  const annees  = [...new Set(data.map(r=>String(r.annee)))].sort();
  const regions = [...new Set(data.map(r=>r.region))].sort();
  const prios   = [...new Set(data.map(r=>r.priorite_dg))].sort();

  if(loading) return <LoadingBlock/>;
  if(error)   return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-cyan-500/30 p-3 flex items-center gap-3"
        style={{background:'linear-gradient(135deg,rgba(6,182,212,.1) 0%,rgba(15,23,42,.8) 100%)'}}>
        <Calendar className="w-5 h-5 text-cyan-400 flex-shrink-0"/>
        <div>
          <p className="text-white font-semibold text-sm">Plan Pluriannuel Formation 2026→2028 — Automatisé depuis GPEC + Gaps compétences + Orientations DG</p>
          <p className="text-slate-400 text-xs">Simulation budgétaire · Priorisation DG · Objectifs certifications · Couverture 10 régions</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users}      label="Agents à former 3 ans" val={totalAgents} color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KpiCard icon={DollarSign} label="Budget besoin (M F)"   val={Math.round(totalBesoin/1e6)} color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30"/>
        <KpiCard icon={CheckCircle} label="Budget alloué (M F)"  val={Math.round(totalAlloue/1e6)} color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KpiCard icon={AlertTriangle} label="Déficit budgétaire (M F)" val={Math.abs(Math.round(deficit/1e6))} color={deficit>0?"text-red-400":"text-green-400"} bg={deficit>0?"bg-red-500/10":"bg-green-500/10"} border={deficit>0?"border-red-500/30":"border-green-500/30"} alert={deficit>0}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget 3 ans */}
        <Card icon={DollarSign} title="Budget formation 3 ans — Besoin vs Alloué (M FCFA)" accent="text-orange-400">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={budgetAnnuel} margin={{top:4,right:8,left:0,bottom:4}} barCategoryGap="30%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="annee" tick={{fill:'#94a3b8',fontSize:10}}/>
              <YAxis tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}M`}/>
              <Tooltip content={<NexTip/>} formatter={(v:any)=>`${v}M F`}/>
              <Legend wrapperStyle={{fontSize:'9px'}}/>
              <Bar dataKey="besoin" name="Budget besoin (M F)"  fill="#f97316" radius={[3,3,0,0]} isAnimationActive animationDuration={900}/>
              <Bar dataKey="alloue" name="Budget alloué (M F)" fill="#10b981" radius={[3,3,0,0]} isAnimationActive animationDuration={1100}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Priorités DG */}
        <Card icon={Star} title="Orientations DG — Agents à former par priorité" accent="text-yellow-400">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={prioDG} layout="vertical" margin={{top:4,right:48,left:8,bottom:4}} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{fill:'#94a3b8',fontSize:9}} allowDecimals={false}/>
              <YAxis type="category" dataKey="prio" tick={{fill:'#cbd5e1',fontSize:8}} width={140}/>
              <Tooltip content={<NexTip/>}/>
              <Bar dataKey="agents" name="Agents à former" radius={[0,4,4,0]} isAnimationActive animationDuration={1000}>
                {prioDG.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400"/>
        {[{v:anneeF,s:setAnneeF,o:annees,p:'Année'},{v:regionF,s:setRegionF,o:regions,p:'Région'},{v:prioF,s:setPrioF,o:prios,p:'Priorité DG'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        {(anneeF||regionF||prioF)&&<button onClick={()=>{setAnneeF('');setRegionF('');setPrioF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} entrées</span>
      </div>

      <Card icon={Calendar} title="Plan pluriannuel détaillé 2026→2028 — Google Sheets live" accent="text-cyan-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight:'380px'}}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['ID','Région','Corps','Grade','Année','Type formation','École','Partenaire','Agents','Sessions','Durée (j)','Budget besoin','Alloué','Écart','Priorité DG','Certif. cible','Taux cible','Statut'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(planPage*REG_PAGE_SIZE,(planPage+1)*REG_PAGE_SIZE).map((r,i)=>(
                  <motion.tr key={r.plan_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                    whileHover={{backgroundColor:'rgba(59,130,246,.05)'}}
                    className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'} ${r.ecart_budget_fcfa<0?'border-l-2 border-l-red-500/50':''}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.plan_id}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.corps}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                    <td className="px-3 py-2 text-center font-bold text-blue-300">{r.annee}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-36 truncate">{r.type_formation}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-36 truncate">{r.ecole}</td>
                    <td className="px-3 py-2">
                      {r.partenaire!=='National'?<span className="text-cyan-400 text-xs flex items-center gap-1"><Globe className="w-3 h-3"/>{r.partenaire}</span>:<span className="text-slate-500">{r.partenaire}</span>}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-blue-300">{r.nb_agents_a_former}</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.nb_sessions_prevues}</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.duree_session_jours}</td>
                    <td className="px-3 py-2 text-right text-orange-400 whitespace-nowrap">{fmtF(r.budget_total_fcfa)}</td>
                    <td className="px-3 py-2 text-right text-green-400 whitespace-nowrap">{fmtF(r.budget_alloue_fcfa)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap"><span className={r.ecart_budget_fcfa>=0?'text-green-400':'text-red-400 font-bold'}>{r.ecart_budget_fcfa>=0?'+':''}{fmtF(r.ecart_budget_fcfa)}</span></td>
                    <td className="px-3 py-2 text-yellow-400 max-w-36 truncate">{r.priorite_dg}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-40 truncate">{r.objectif_certif}</td>
                    <td className="px-3 py-2 text-center text-purple-400">{r.taux_cible_pct}%</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${
                        r.statut_plan==='Approuvé'?'bg-green-500/10 border-green-500/30 text-green-400':
                        r.statut_plan==='En cours'?'bg-blue-500/10 border-blue-500/30 text-blue-400':
                        r.statut_plan==='Complété'?'bg-purple-500/10 border-purple-500/30 text-purple-400':
                        'bg-slate-500/10 border-slate-500/30 text-slate-400'}`}
                        animate={r.statut_plan==='En cours'?{opacity:[1,.7,1]}:{}}
                        transition={{duration:1.5,repeat:Infinity}}>
                        {r.statut_plan}
                      </motion.span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const TrainingAdvancedTabs = () => {
  const [tab,setTab] = React.useState<'parcours'|'catalogue'|'gaps'|'plan'>('parcours');
  const TABS = [
    {k:'parcours',  label:'Parcours individuel',    icon:Users,    accent:'text-blue-400'},
    {k:'catalogue', label:'Catalogue formations',   icon:BookOpen, accent:'text-yellow-400'},
    {k:'gaps',      label:'Gaps compétences',       icon:Target,   accent:'text-red-400'},
    {k:'plan',      label:'Plan pluriannuel 3 ans', icon:Calendar, accent:'text-cyan-400'},
  ];
  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-blue-900/40"/>
        <span className="text-slate-400 text-xs font-medium px-3">Modules avancés — Intelligence Formation · Google Sheets live</span>
        <div className="flex-1 h-px bg-blue-900/40"/>
      </div>
      <div className="flex items-center gap-1 rounded-xl p-1 flex-wrap"
        style={{background:'linear-gradient(135deg,rgba(30,41,59,.8) 0%,rgba(15,23,42,.6) 100%)',backdropFilter:'blur(8px)',border:'1px solid rgba(59,130,246,.15)'}}>
        {TABS.map(t=>(
          <motion.button key={t.k} onClick={()=>setTab(t.k as any)}
            whileHover={{scale:1.03}} whileTap={{scale:.97}}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab===t.k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </motion.button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        {tab==='parcours'  &&<motion.div key="p"  initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><ParcoursAgent/></motion.div>}
        {tab==='catalogue' &&<motion.div key="c"  initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><CataloguePlusFormations/></motion.div>}
        {tab==='gaps'      &&<motion.div key="g"  initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><GapsCompetences/></motion.div>}
        {tab==='plan'      &&<motion.div key="pl" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}} transition={{duration:.3}}><PlanPluriannel/></motion.div>}
      </AnimatePresence>
    </div>
  );
};
